/**
 * SINGLE SOURCE OF TRUTH FOR SITE-WIDE METADATA.
 *
 * WORDPRESS / ACF MAPPING
 * -----------------------
 * Everything in this file maps to an ACF "Options Page" (Site Settings),
 * NOT to an individual page. Recommended ACF group: `site_settings`.
 */

export interface SocialProfile {
  platform: 'facebook' | 'instagram' | 'youtube' | 'spotify'
  label: string
  url: string
}

export const SITE = {
  /** ACF: site_settings_name */
  name: "Milangela's Soul Garden",
  /** ACF: site_settings_short_name */
  shortName: 'Soul Garden',
  /** ACF: site_settings_url — no trailing slash */
  url: 'https://milangelassoulgarden.com',
  /** ACF: site_settings_default_locale */
  locale: 'en_US',
  /** ACF: site_settings_language */
  language: 'en',
  /** ACF: site_settings_tagline */
  tagline: 'A space to return to yourself.',
  /** ACF: site_settings_description — used as the homepage / fallback meta description */
  description:
    'A soulful space of support on your path back to yourself. Reconnect through Biofield Tuning sound therapy, guided meditation and compassionate presence with Branka Androjna.',
  /** ACF: site_settings_logo */
  logo: 'https://milangelassoulgarden.com/wp-content/uploads/2022/07/01-MILANGELA-01.png',
  /** ACF: site_settings_default_share_image — 1200x630 recommended */
  defaultImage:
    'https://cdn.magicpatterns.com/uploads/rNSyYD7BhQbyjDzyPNZ74D/milangela_home.jpg',
  /** ACF: site_settings_email */
  email: 'branka@milangelassoulgarden.com',
  /** ACF: site_settings_price_range */
  priceRange: '€15–€100',
  /** ACF: site_settings_area_served */
  areaServed: 'Worldwide (online) and Slovenia (in person)',
} as const

/**
 * The practitioner behind the brand.
 * ACF: site_settings_practitioner_{name,alternate_name,job_title,image,bio}
 */
export const PRACTITIONER = {
  name: 'Branka Androjna',
  alternateName: 'Milangela',
  jobTitle: 'Certified Biofield Tuning Practitioner & Meditation Guide',
  image:
    'https://cdn.magicpatterns.com/uploads/pn7FFnELSjQsU2kEbzrUJM/milangela_once_a_dream.webp',
  bio: 'Certified Biofield Tuning Practitioner, meditation guide and compassionate companion, supporting people as they reconnect with themselves through sound, stillness and presence.',
  knowsAbout: [
    'Biofield Tuning',
    'Sound therapy',
    'Guided meditation',
    'Energy medicine',
    'Mindfulness',
    'Emotional wellbeing',
  ],
  credentials: [
    'Bachelor of Arts in English',
    'Certified Biofield Tuning Practitioner (Eileen Day McKusick Method)',
    'Essentials of Energy Medicine (Donna Eden)',
    'Advanced Week-long Retreats with Dr. Joe Dispenza (Brighton & Malta)',
    'Path to Freedom – Ali Daniel',
    'London Real – Broadcast Yourself Training',
  ],
} as const

/**
 * ACF: site_settings_social_links (repeater: platform, label, url)
 * NOTE: replace the placeholder `#` values with the real profile URLs
 * before going live — empty social links weaken the sameAs schema signal.
 */
export const SOCIAL_PROFILES: SocialProfile[] = [
  {
    platform: 'facebook',
    label: 'Facebook',
    url: 'https://www.facebook.com/milangelassoulgarden',
  },
  {
    platform: 'instagram',
    label: 'Instagram',
    url: 'https://www.instagram.com/milangelassoulgarden',
  },
  {
    platform: 'youtube',
    label: 'YouTube',
    url: 'https://www.youtube.com/@milangelassoulgarden',
  },
  {
    platform: 'spotify',
    label: 'Spotify',
    url: 'https://creators.spotify.com/pod/profile/milangela/',
  },
]

/** Absolute URL helper — schema and canonical tags must never use relative paths. */
export function absoluteUrl(path = '/'): string {
  if (path.startsWith('http')) return path
  return `${SITE.url}${path.startsWith('/') ? path : `/${path}`}`
}
